/* eslint-disable  @typescript-eslint/no-explicit-any */
import moment from "moment";
import { ILookupItem, LookupItem } from "@/model/LookupItem";
import { IClientLookup } from "@/model/Client";
import { ISiteLookup } from "@/model/Site";
import { Route } from "vue-router";
import Store from "@/store/store";
import { UserRole } from "@/model/Enums";
import { User } from "@/model/User";

module Utils {

    export const sleep = (milliseconds: number): Promise<any> => {
        return new Promise(resolve => setTimeout(resolve, milliseconds));
    }

    export function isDevelopment(): boolean {
        return process.env.NODE_ENV === "development";
    }

    //
    // -- strings
    //

    export function isEmptyOrWhitespace(text: string): boolean {
        if (!text) return true;
        // see if any non-whitespace
        return !(/\S/.test(text));
    }

    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    export function isValidEmail(email: string): boolean {
        return emailRegex.test(String(email).toLowerCase());
    }

    const tagRegex = /(<([^>]+)>)/g;
    export function looksLikeHtml(text: string): boolean {
        const count = ((text || "").match(tagRegex) || []).length;
        return count > 2; // how many tags make it html?
    }


    export function dashify(text: string) {
        if(!text) return "";
        const regex = /[^a-zA-Z0-9]/g;
        return text.toLowerCase().replace(regex, "-");
    }

    export function pad(value: number, width: number, z?: string) {
        z = z || "0";
        const n = value + "";
        return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
    }

    // https://stackoverflow.com/questions/4149276/how-to-convert-camelcase-to-camel-case
    export function spacify(camelCase: string) {
        // insert a space before all caps
        return camelCase.replace(/([A-Z])/g, ' $1');
        // uppercase the first character
        //.replace(/^./, function(str){ return str.toUpperCase(); })
    }

    // string delimited with comma, semicolon or pipe - remove any blanks
    // (e.g. delimited email list)
    // (don't allow spaces 'cos mailbox spec can contain space, e.g., "Fred Filitstone" <fred@bedrock.old>)
    // (limitation - if the list contains strings which contain commas, e.g., mailbox display names, we're a bit f***ed here)
    export function splitDelimited(text: string): Array<string> {
        if(!text) return [];
        return text
            .replaceAll(",", ";")
            .replaceAll("|", ";")
            .split(";")
            .map(i => i.trim())
            .filter(i => !isEmptyOrWhitespace(i));
    }

    // extract email and display name from, e.g., "Fred Filitstone" <fred@bedrock.old>
    export const parseMailbox = (text: string): { email: string; name: string; } => {
        const value = { email: "", name: "" }
        if (Utils.isEmptyOrWhitespace(text)) return value;
        text = text.trim();
        const ltAt = text.indexOf("<");
        const gtAt = text.indexOf(">");
        const q1At = text.indexOf("\"");
        //const q2At = q1At === -1 ? -1 : text.indexOf("\"", q1At + 1);
        if (ltAt === -1 && q1At === -1 && Utils.isValidEmail(text)) {
            value.email = text;
            return value;
        }
        if (ltAt !== -1 && gtAt !== -1 && gtAt - ltAt > 5) {
            // TODO - name could be "Flintstone, Fred" - but do we care?
            // (actually - that would break the splitDelimited part!)
            value.name = text.substring(0, ltAt).replaceAll("\"", "").trim();
            value.email = text.substring(ltAt + 1, gtAt);
            return Utils.isValidEmail(value.email)
                ? value
                : { email: "", name: "" } ;
        }
        return value;
    }

    export const compareStrings = (a: string, b: string) => {
        if (!a && !b) return 0;
        if (!a) return -1;
        if (!b) return 1;
        const al = a.toLowerCase();
        const bl = b.toLowerCase();
        return al > bl ? 1 : bl > al ? -1 : 0;
    }

    //
    // -- GUIDs
    //

    export const emptyGuidValue = "00000000-0000-0000-0000-000000000000";

    export const dummyGuidValue = "FFFFFFFF-FFFF-FFFF-FFFF-FFFFFFFFFFFF";

    // a GUID that is sortable in SQL server in date order
    export function newGuid(): string {
        function s4(): string {
            return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
        }
        const dateHex = `000000000000${new Date().getTime().toString(16)}`;
        return `${s4()}${s4()}-${s4()}-4${s4().substring(1, 4)}-${s4()}-${dateHex.substring(dateHex.length - 12)}`;
    }

    export function isEmptyId(id: any): boolean {
        if (!id) return true;
        if (typeof id === "string") {
            return id === emptyGuidValue || isEmptyOrWhitespace(id) || id === "0";
        }
        else if (typeof id === "number") {
            return id === 0;
        }
        return false;
    }

    export function isGuid(text: string): boolean {
        if (!text) return false;
        const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        return pattern.test(text);
    }

    // a random key but much shorter than a full guid
    export function createKey(length: number): string {
        let key = "";
        const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        const charactersLength = characters.length;
        for ( let i = 0; i < length; i++ ) {
            key += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return key;
    }

    //
    // -- enums
    //

    // https://stackoverflow.com/questions/50158272/what-is-the-type-of-an-enum-in-typescript
    type Enum<E> = Record<keyof E, number | string> & { [k: number]: string };

    export function enumToLookups<E extends Enum<E>>(theEnum: E): Array<LookupItem> {
        return Object.keys(theEnum)
            .filter(value => isNaN(Number(value)) === false)
            .map(num => LookupItem.createLookup(+num, camelCaseAddSpaces(theEnum[+num] as string))) ;
    }

    export function camelCaseAddSpaces(text: string): string {
        if (!text) return "";
        const result = text.replace(/([A-Z])/g, " $1");
        return result.charAt(0).toUpperCase() + result.slice(1);
    }

    //
    // -- dates
    //

    export const emptyDateValue = -62135596800000;

    export const nth = (n: number) => {
        if (n > 3 && n < 21) return "th";
        switch (n % 10) {
          case 1:  return "st";
          case 2:  return "nd";
          case 3:  return "rd";
          default: return "th";
        }
    }

    export function endOfToday(): Date {
        return new Date(new Date().setHours(23,59,59,0));
    }

    export function hasTime(d: Date): boolean {
        if (!d) return false;
        return d.getHours() > 0 || d.getMinutes() > 0;
    }

    export function whenText(d: any): string {
        const dte = moment(d);
        if (!dte.isValid() || dte.year() < 1753) { return "- - -"; }
        const daysDiff = moment().startOf("day").diff(moment(d).startOf("day"), "days");
        if (daysDiff < 0) { return "in the future"; }
        if (daysDiff === 1) { return "yesterday"; }
        if (daysDiff > 7) { return moment(d).format("DD MMM YYYY"); }
        if (daysDiff > 1) { return `${daysDiff} days ago`; }
        const hoursDiff = moment().diff(dte, "hours");
        if (hoursDiff === 1) { return "an hour ago"; }
        if (hoursDiff > 1) { return `${hoursDiff} hours ago`; }
        const minsDiff = moment().diff(dte, "minutes");
        if (minsDiff === 1) { return "a minute ago"; }
        if (minsDiff > 1) { return `${minsDiff} mins ago`; }
        return "just now";
    }

    const monthsShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    export function dateText(d: any, emptyValue: string = "- - -"): string {
        if(typeof d === "string") d = parseDateText(d);
        if(!hasDateValue(d)) return emptyValue;
        const date = d as Date;
        const day = date.getDate();
        return `${day}${nth(day)} ${monthsShort[date.getMonth()]} ${date.getFullYear()}`;
    }

    export function dateTimeText(d: any, emptyValue: string = "- - -"): string {
        if(!hasDateValue(d)) return emptyValue;
        const date = d as Date;
        return hasTime(date)
            ? `${dateText(date)} ${pad(date.getHours(), 2)}:${pad(date.getMinutes(), 2)}`
            : dateText(date)
    }

    export const pickerDateText = (value: Date|null): string => {
        return value === null || !hasDateValue(value)
            ? ""
            : `${value.getFullYear()}-${pad(value.getMonth() + 1, 2)}-${pad(value.getDate(), 2)}`;
    }

    export const pickerDateTimeText = (value: Date|null): string => {
        // yyyy-MM-ddTHH:mm:ss
        return value === null || !hasDateValue(value)
            ? ""
            : `${value.getFullYear()}-${pad(value.getMonth() + 1, 2)}-${pad(value.getDate(), 2)}T${pad(value.getHours(), 2)}:${pad(value.getMinutes(), 2)}:${pad(value.getSeconds(), 2)}`;
    }

    export const timeStampText = (): string => {
        const value = new Date();
        return `${value.getFullYear()}-${pad(value.getMonth() + 1, 2)}-${pad(value.getDate(), 2)}-${pad(value.getHours(), 2)}-${pad(value.getMinutes(), 2)}-${pad(value.getSeconds(), 2)}`;
    }

    export const parseDateText = (text: string|null|undefined) => {
        if (typeof text !== "string") return new Date(emptyDateValue);
        if (text.indexOf("-") > -1) {
            const parts = text.split("-");
            if (parts.length !== 3) return new Date(emptyDateValue);
            const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
            return date;
        }
        else {
            const parts = text.split("/");
            if (parts.length !== 3) return new Date(emptyDateValue);
            const date = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            return date;
        }
    }

    export const parseDateTimeText = (text: string|null|undefined): Date => {
        // trying to not use Moment so much these days - but f**k it, it's in the project and it makes it easier.
        if (typeof text !== "string") return new Date(emptyDateValue);
        const value = moment(text);
        return value.isValid() ? value.toDate() : new Date(emptyDateValue);
    }

    export function isDate(d: any): boolean {
        if (!d) return false;
        return typeof d.getMonth === "function";
    }

    export function hasDateValue(d: any): boolean {
        return isDate(d) && (+(d)) > -6847804800000; // 1753 = min SQL date
    }

    //
    // -- arrays (lookups)
    //

    // Lookups may contain archived items and possibly a default (none) item (or not)
    // For drop-down lists, we want a specific default item and to remove any archived items
    export function selectOptions(allItems: Array<ILookupItem>, defaultText?: string): Array<ILookupItem> {
        if (!defaultText) defaultText = "Please choose...";
        const defaultItem = LookupItem.createLookup(0, defaultText);
        if (!allItems) return [defaultItem];
        return [defaultItem, ...optionsFilter(allItems)];
    }

    export function optionsFilter(allItems: Array<ILookupItem>): Array<ILookupItem> {
        return allItems.filter(lu => !isEmptyId(lu.id) && !hasDateValue(lu.deleted));
    }

    export function clientOptionsFilter(allItems: Array<IClientLookup>): Array<IClientLookup> {
        const user = Store.state.signedInUser;
        const clientIDs = user?.clientIDs ?? [];
        const isAdmin = user?.role === UserRole.Admin;
        return allItems.filter(c =>
            !isEmptyId(c.id) &&
            !hasDateValue(c.deleted) &&
            (isAdmin || clientIDs.indexOf(c.id) > -1)
        )
    }

    export function siteOptionsFilter(allItems: Array<ISiteLookup>, clientID: string): Array<ISiteLookup> {
        return allItems.filter(
            s => !isEmptyId(s.id) &&
            !hasDateValue(s.deleted) &&
            (isEmptyId(clientID) || s.clientID === clientID));
    }

    export function unique<T>(arr: Array<T>): Array<T> {
        const a = arr.concat();
        for(let i=0; i<a.length; ++i) {
            for(let j=i+1; j<a.length; ++j) {
                if(a[i] === a[j])
                    a.splice(j--, 1);
            }
        }
        return a;
    }

    //
    // -- stuff...
    //

    // this.$route.query.whatever returns string|(string|null)[] - so pick that apart...
    export function getUrlQueryValue(route: Route, key: string): string {
        const value = route.query[key];
        if(typeof value === "string") return value;
        if(Array.isArray(value) && value.length > 0) return value[0] ?? "";
        return "";
    }

    // helper function for lookups requests, etc.
    // (this is somewhat redundant as filters are applied on the server to prevent unauthorised access)
    export function addClientQueryParameters(url: string, user?: User|null): string {
        if(!user) {
            user = Store.getters.signedInUser as User|null;
        }
        if(!user) {
            return `${url}?clientID=${dummyGuidValue}`;
        }
        let isFirst = true;
        for (const clientID of user.clientIDs) {
            url += `${isFirst ? "?" : "&"}clientID=${clientID}`;
            isFirst = false;
        }
        return url;
    }

    export function debounce<F extends (...params: any[]) => void>(func: F, wait: number, immediate = false) {
        let timeout: number | undefined;
        return function(this: any, ...args: any[]) {
            const later = () => {
                timeout = undefined;
                if (!immediate) func.apply(this, args);
            };
            const callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) func.apply(this, args);
        } as F;
    }

    export function resetObject(obj: any) {
        for (const i in obj) {
            if (obj.hasOwnProperty(i)) {
                const type = typeof obj[i];
                //if (obj[i] instanceof Guid) {
                //    obj[i] = Guid.createEmpty();
                //}
                if (Object.prototype.toString.call(obj[i]) === "[object Date]") {
                    obj[i] = null;
                }
                if (type === "object") {
                    // recursive call
                    resetObject(obj[i]);
                }
                else if (type === "number" || type === "bigint") {
                    obj[i] = 0;
                }
                else if (type === "string") {
                    obj[i] = "";
                }
                else if (type === "boolean") {
                    obj[i] = false;
                }
                else if (Array.isArray(obj[i])) {
                    obj[i] = [];
                }
            }
        }
    }

    export function fileSize(bytes: number): string {
        const decimals = 1; // can change this as required...
        let text = "0 bytes";
        if (bytes > 0) {
            const k = 1000; // or 1024 for KiB, MiB, etc.
            const dm = decimals + 1 || 3;
            const sizes = ["bytes", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            text = parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
        }
        return text;
    }

    // const normaliseDegrees = (value: number|string|null): number => {
    //     let num = Number(value ?? 0);
    //     if(isNaN(num)) return 0;
    //     while(num > 180) num -= 360;
    //     while(num < -180) num += 360;
    //     return num;
    // };

    export const roundDegrees = (value: number|string|null) => Math.round((Number(value) + Number.EPSILON) * 100000) / 100000;

    export const hashNumber = (text: string) => {
        let hash = 0;
        let i = 0;
        let chr = 0;
        if (!text || text.length === 0) return hash;
        for (i = 0; i < text.length; i++) {
            chr = text.charCodeAt(i);
            // tslint:disable-next-line:no-bitwise
            hash = ((hash << 5) - hash) + chr;
            // tslint:disable-next-line:no-bitwise
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }
}

export default Utils;
